import type { GroupedQuotes, Filters, Service, ProviderQuote, Quote } from '../types'
import { getTransferTime, compareTransferTime } from './transfer-time'
import {
  getProviderExposure,
  getSpotlightInfo,
  providerHasNegativeExposure,
  providerHasPositiveExposure,
} from './spotlight'
import { getAmount, getPromo } from './pricing'

function getValueProps(quotes: Quote[]) {
  const messages = quotes.flatMap(({ valueProps }) =>
    valueProps?.length ? valueProps[valueProps.length - 1].messageKeys : [],
  )
  return messages.length ? [...new Set(messages)] : null
}

function getCustomMessages(quotes: Quote[]) {
  const allMessages = quotes.flatMap(({ messages }) => messages ?? []).reverse()
  return {
    information: allMessages.find((m) => m.type === 'INFORMATION')?.messageKeys,
    caution: allMessages.find((m) => m.type === 'CAUTION')?.messageKeys,
    warning: allMessages.find((m) => m.type === 'WARNING')?.messageKeys,
  }
}

function getCheapestService(services: Service[]) {
  let bestAmount = services[0].amount
  let cheapestIndex = 0
  for (let index = 1; index < services.length; index++) {
    if (services[index].amount > bestAmount) {
      bestAmount = services[index].amount
      cheapestIndex = index
    }
  }
  return services[cheapestIndex]
}

function getCheapestProviderIndex(groupedQuotes: GroupedQuotes[]) {
  let bestAmount = groupedQuotes[0].cheapestService.amount
  let cheapestIndex = 0
  for (let index = 1; index < groupedQuotes.length; index++) {
    const amount = groupedQuotes[index].cheapestService.amount
    if (amount > bestAmount) {
      bestAmount = amount
      cheapestIndex = index
    }
  }
  return cheapestIndex
}

function filterAndSortQuotes(quotes: Quote[]): Quote[] {
  const q = quotes.slice(0)
  q.sort((a, b) => getAmount(b) - getAmount(a) || compareTransferTime(a, b))

  // Sorting before is important, the filter only checks previous quotes in the list
  const filtered = q.filter((quote, ind) => {
    for (let i = ind - 1; i >= 0; i--) {
      if (
        // TODO check if this case exists in real life
        quote.payin === q[i].payin &&
        getAmount(quote) <= getAmount(q[i]) &&
        quote.transferTime.min + quote.transferTime.max >=
          q[i].transferTime.min + q[i].transferTime.max
      ) {
        // Filter out if slower and more expensive than any before it
        return false
      }
    }
    return true
  })

  return filtered
}

export function computeServices(quotes: Quote[]): Service[] {
  const filteredQuotes = filterAndSortQuotes(quotes)

  const services: Service[] = []
  for (const quote of filteredQuotes) {
    const promo = getPromo(quote)
    const amount = getAmount(quote)
    const transferTime = getTransferTime(quote.transferTime)
    const service = services.find(
      (g) => g.amount === amount && g.transferTime.id === transferTime.id,
    )
    if (service) {
      service.payins.push(quote.payin)
      if (
        !service.promotion ||
        (promo && service.promotion.receivedAmount < promo.receivedAmount)
      ) {
        service.promotion = promo
      }
    } else {
      services.push({ amount, transferTime, payins: [quote.payin], promotion: promo, quote })
    }
  }

  return services
}

export function processQuotes(
  providerQuotes: ProviderQuote[],
  filters: Filters,
  cheapestOver3Months: { provider: { slug: string }; percentage: number } | null,
): GroupedQuotes[] {
  const filteredQuotes = providerQuotes
    .map((pq) => {
      let quotes = filters.tab?.(pq) ?? pq.quotes
      if (filters.usedProviders?.includes(pq.psp.slug)) {
        quotes = quotes.map((quote) => ({
          ...quote,
          promos: quote.promos.filter((p) => !p.newCustomersOnly),
        }))
      }
      if (filters.payin) {
        quotes = quotes.filter((q) => filters.payin?.includes(q.payin))
      }
      if (filters.hidePromos) {
        quotes = quotes.map((quote) => ({
          ...quote,
          promos: [],
        }))
      }
      return { psp: pq.psp, quotes }
    })
    .filter((pq) => pq.quotes.length)

  const exposures = filteredQuotes.map((pq) => getProviderExposure(pq, filters) ?? 0)
  const medianExposure = median(exposures)
  const positiveExposureResultsExist = filteredQuotes.some((pq) =>
    providerHasPositiveExposure(pq.quotes, filters),
  )

  const results: GroupedQuotes[] = filteredQuotes
    .filter((pq) => {
      return (
        filters.usedProviders?.includes(pq.psp.slug) ||
        !positiveExposureResultsExist ||
        !providerHasNegativeExposure(pq.quotes, filters)
      )
    })
    .map(({ psp, quotes }) => {
      const exposure = getProviderExposure({ psp, quotes }, filters)
      const services = computeServices(quotes)
      if (!services.length) {
        return null
      }
      return {
        psp,
        exposure,
        services,
        cheapestService: getCheapestService(services),
        valueProps: exposure && exposure > medianExposure ? getValueProps(quotes) : null,
        customMessages: getCustomMessages(quotes),
      }
    })
    .filter((r): r is GroupedQuotes => r !== null)

  if (!results.length) {
    return results
  }

  results.sort(
    (a, b) =>
      b.services[0].amount - a.services[0].amount ||
      compareTransferTime(a.services[0], b.services[0]),
  )

  const cheapestIndex = getCheapestProviderIndex(results)
  results[cheapestIndex].bestDeal = true

  const spotlightInfo = getSpotlightInfo(results, cheapestOver3Months)
  if (spotlightInfo) {
    results[spotlightInfo.index].label = spotlightInfo.label
    if (spotlightInfo.percentage) {
      results[spotlightInfo.index] = {
        ...results[spotlightInfo.index],
        historicalData: {
          percentage: spotlightInfo.percentage,
        },
      }
    }
  }
  return results
}
